import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, Outlet, Navigate } from 'react-router-dom';
import NotificationBell from './NotificationBell';
import ChatWidget from './ChatWidget';
import { applyThemeColors, getContrastText } from './ThemeColorsCard';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import {
  LayoutDashboard,
  Package,
  Users,
  UserCog,
  Truck,
  FileText,
  MessageCircle,
  BarChart3,
  Upload,
  Settings,
  LogOut,
  Sun,
  Moon,
  Menu,
  X,
  StickyNote,
  ChevronLeft,
  ChevronRight,
  User,
  Edit,
  HelpCircle,
  TrendingUp,
  PanelLeft,
  Bell,
  ArrowLeftRight,
  Receipt,
  DollarSign,
  Sparkles
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Layout = () => {
  const { user, loading, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  
  // Estado do sidebar - colapsado ou expandido
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  
  // Menu do usuário
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);
  const [novosPedidos, setNovosPedidos] = useState(0);
  const [adminThemeColors, setAdminThemeColors] = useState({});

  // Carrega e aplica as cores personalizadas do admin
  useEffect(() => {
    axios.get(`${API}/site-settings`).then(r => {
      const c = r.data?.admin_theme_colors || {};
      setAdminThemeColors(c);
      applyThemeColors(c);
    }).catch(() => {});
  }, []);

  // Polling novos pedidos
  useEffect(() => {
    const fetchNovos = () => {
      axios.get(`${API}/admin/novos-pedidos`).then(r => setNovosPedidos(r.data?.count || 0)).catch(() => {});
    };
    fetchNovos();
    const interval = setInterval(fetchNovos, 30000);
    return () => clearInterval(interval);
  }, []);

  // Fechar menu do usuário ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Aguarda o AuthContext terminar de carregar do localStorage
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Carregando...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    // Sem login → manda para o catálogo público (não revela existência do admin)
    return <Navigate to="/" replace />;
  }

  // BUG FIX SEG: só admin pode acessar a área /dashboardadminromaneio/*.
  // Vendedor/Cliente é redirecionado pro vendor portal (impede que sessão de vendor abra admin).
  if (user.role && user.role !== 'admin') {
    if (user.role === 'vendedor') {
      return <Navigate to="/vendedor/dashboard" replace />;
    }
    return <Navigate to="/vendedor/login" replace />;
  }

  const navigation = [
    { name: 'Dashboard', path: '/dashboardadminromaneio/dashboard', icon: LayoutDashboard },
    { name: 'Analytics', path: '/dashboardadminromaneio/analytics', icon: TrendingUp },
    { name: 'Editor do Site', path: '/dashboardadminromaneio/site-editor', icon: PanelLeft, badge: 'NOVO' },
    { name: 'Produtos', path: '/dashboardadminromaneio/products', icon: Package },
    { name: 'Clientes', path: '/dashboardadminromaneio/clients', icon: Users },
    { name: 'Vendedores', path: '/dashboardadminromaneio/vendedores', icon: UserCog },
    { name: 'Transportadoras', path: '/dashboardadminromaneio/carriers', icon: Truck },
    { name: 'Romaneios', path: '/dashboardadminromaneio/romaneios', icon: FileText },
    { name: 'Pagamentos', path: '/dashboardadminromaneio/pagamentos', icon: DollarSign },
    { name: 'Abatimentos', path: '/dashboardadminromaneio/abatimento-requests', icon: ArrowLeftRight },
    { name: 'Comprovantes', path: '/dashboardadminromaneio/comprovantes-review', icon: Receipt },
    { name: 'Usuários', path: '/dashboardadminromaneio/usuarios', icon: Users },
    { name: 'Notas', path: '/dashboardadminromaneio/notas', icon: StickyNote },
    { name: 'Relatórios', path: '/dashboardadminromaneio/relatorios', icon: BarChart3 },
    { name: 'Importação', path: '/dashboardadminromaneio/importacao', icon: Upload },
    { name: 'WhatsApp', path: '/dashboardadminromaneio/whatsapp', icon: MessageCircle },
    { name: 'Chat', path: '#chat', icon: MessageCircle, action: 'openChat' },
    { name: 'IA (Modelos)', path: '/dashboardadminromaneio/ai-settings', icon: Sparkles, badge: 'NOVO' },
    { name: 'Configurações', path: '/dashboardadminromaneio/configuracoes', icon: Settings },
  ];

  // Logo com fundo branco para tema claro e logo clara para tema escuro
  const logoUrl = theme === 'light' 
    ? 'https://customer-assets.emergentagent.com/job_stockwise-app-17/artifacts/wbwkqvrv_Claro.png'
    : 'https://customer-assets.emergentagent.com/job_stockwise-app-17/artifacts/waabplrj_Claro.png';

  // Gerar iniciais do usuário para avatar
  const getUserInitials = (name) => {
    if (!name) return 'U';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // Largura do sidebar baseada no estado
  const sidebarWidth = sidebarCollapsed ? 'w-20' : 'w-64';

  return (
    <div
      className="flex h-screen overflow-hidden bg-background"
      style={adminThemeColors.bg ? { background: adminThemeColors.bg, color: getContrastText(adminThemeColors.bg) } : undefined}
    >
      {/* Sidebar */}
      <aside
        className={`
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:translate-x-0
          fixed inset-y-0 left-0 z-50 
          ${sidebarWidth}
          bg-secondary text-secondary-foreground 
          transition-all duration-300 ease-in-out 
          lg:static flex flex-col 
          border-r border-border/10 shadow-2xl
        `}
        data-testid="sidebar"
      >
        {/* Logo e Botão Recolher */}
        <div className={`relative flex items-center ${sidebarCollapsed ? 'justify-center' : 'justify-center'} h-24 px-4 border-b border-border/10`}>
          {!sidebarCollapsed && (
            <img src={logoUrl} alt="Santa Ana" className="h-20 w-auto object-contain" />
          )}
          {sidebarCollapsed && (
            <img src={logoUrl} alt="Santa Ana" className="h-14 w-14 object-contain rounded-lg" />
          )}
          
          {/* Botão de colapsar - visível apenas em desktop */}
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className={`hidden lg:flex p-2 rounded-lg hover:bg-white/10 transition-colors text-white/70 hover:text-white ${sidebarCollapsed ? '' : 'absolute right-2 top-1/2 -translate-y-1/2'}`}
            title={sidebarCollapsed ? 'Expandir menu' : 'Recolher menu'}
            data-testid="collapse-sidebar-btn"
          >
            {sidebarCollapsed ? (
              <ChevronRight className="w-5 h-5" />
            ) : (
              <ChevronLeft className="w-5 h-5" />
            )}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4 px-2">
          {navigation.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            if (item.action === 'openChat') {
              return (
                <button
                  key={item.path}
                  onClick={() => { setMobileOpen(false); if (window.openChat) window.openChat(); }}
                  data-testid={`nav-${item.name.toLowerCase()}`}
                  title={sidebarCollapsed ? item.name : undefined}
                  className={`w-full flex items-center gap-3 ${sidebarCollapsed ? 'justify-center px-2' : 'px-4'} py-3 rounded-lg mx-1 my-1 transition-all duration-200 hover:bg-white/10 text-white group relative`}
                >
                  <Icon className="w-5 h-5 flex-shrink-0" />
                  {!sidebarCollapsed && <span className="truncate text-left">{item.name}</span>}
                </button>
              );
            }
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.name.toLowerCase()}`}
                onClick={() => setMobileOpen(false)}
                title={sidebarCollapsed ? item.name : undefined}
                className={`
                  flex items-center gap-3 
                  ${sidebarCollapsed ? 'justify-center px-2' : 'px-4'} 
                  py-3 rounded-lg mx-1 my-1 
                  transition-all duration-200 hover:bg-white/10 text-white 
                  ${isActive ? 'bg-primary text-primary-foreground font-semibold shadow-lg shadow-orange-500/20' : ''}
                  group relative
                `}
              >
                <Icon className="w-5 h-5 flex-shrink-0" />
                {!sidebarCollapsed && <span className="truncate">{item.name}</span>}
                {!sidebarCollapsed && item.badge && (
                  <span className="ml-auto px-2 py-0.5 text-xs font-bold bg-green-500 text-white rounded-full animate-pulse">
                    {item.badge}
                  </span>
                )}
                
                {/* Tooltip para modo colapsado */}
                {sidebarCollapsed && (
                  <div className="absolute left-full ml-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all whitespace-nowrap z-50 shadow-lg">
                    {item.name}
                    {item.badge && (
                      <span className="ml-2 px-1.5 py-0.5 text-xs bg-green-500 rounded-full">
                        {item.badge}
                      </span>
                    )}
                  </div>
                )}
              </Link>
            );
          })}
        </nav>

        {/* User Section - Redesenhado */}
        <div className="border-t border-border/10 p-3" ref={userMenuRef}>
          <div className="relative">
            <button
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              className={`
                w-full flex items-center gap-3 
                ${sidebarCollapsed ? 'justify-center' : ''} 
                p-2 rounded-lg hover:bg-white/10 transition-colors
              `}
              data-testid="user-menu-btn"
            >
              {/* Avatar - mostra foto se tiver, senão iniciais */}
              {user.profileImage ? (
                <img 
                  src={user.profileImage} 
                  alt={user.name} 
                  className="w-10 h-10 rounded-full object-cover flex-shrink-0 shadow-lg border-2 border-primary/30"
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-bold text-sm flex-shrink-0 shadow-lg">
                  {getUserInitials(user.name)}
                </div>
              )}
              
              {/* Nome do usuário - só aparece quando expandido */}
              {!sidebarCollapsed && (
                <div className="flex-1 text-left min-w-0">
                  <p className="text-sm font-medium text-white truncate">{user.name}</p>
                </div>
              )}
              
              {/* Indicador de menu */}
              {!sidebarCollapsed && (
                <ChevronRight className={`w-4 h-4 text-white/50 transition-transform ${userMenuOpen ? 'rotate-90' : ''}`} />
              )}
            </button>

            {/* Menu Dropdown do Usuário */}
            {userMenuOpen && (
              <div 
                className={`
                  absolute bottom-full mb-2 
                  ${sidebarCollapsed ? 'left-full ml-2' : 'left-0 right-0'}
                  bg-card border border-border rounded-lg shadow-xl overflow-hidden
                  animate-in fade-in slide-in-from-bottom-2 duration-200
                  min-w-[200px]
                `}
                style={{ zIndex: 100 }}
              >
                {/* Header do menu */}
                <div className="px-4 py-3 bg-muted/50 border-b border-border">
                  <p className="font-medium text-sm text-foreground">{user.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                </div>
                
                {/* Opções do menu */}
                <div className="py-1">
                  <Link
                    to="/dashboardadminromaneio/perfil"
                    onClick={() => {
                      setUserMenuOpen(false);
                      setMobileOpen(false);
                    }}
                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground hover:bg-accent transition-colors"
                  >
                    <Edit className="w-4 h-4" />
                    Editar Perfil
                  </Link>
                  <Link
                    to="/dashboardadminromaneio/configuracoes"
                    onClick={() => {
                      setUserMenuOpen(false);
                      setMobileOpen(false);
                    }}
                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground hover:bg-accent transition-colors"
                  >
                    <Settings className="w-4 h-4" />
                    Configurações
                  </Link>
                  <Link
                    to="/dashboardadminromaneio/ajuda"
                    onClick={() => {
                      setUserMenuOpen(false);
                      setMobileOpen(false);
                    }}
                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground hover:bg-accent transition-colors"
                  >
                    <HelpCircle className="w-4 h-4" />
                    Ajuda
                  </Link>
                </div>
                
                {/* Separador e Logout */}
                <div className="border-t border-border py-1">
                  <button
                    onClick={() => {
                      setUserMenuOpen(false);
                      logout();
                    }}
                    data-testid="logout-btn"
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                  >
                    <LogOut className="w-4 h-4" />
                    Sair da Conta
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Bar */}
        <header className="h-16 bg-card border-b border-border flex items-center justify-between px-3 sm:px-6 shadow-sm gap-2">
          {/* Botão Mobile Menu */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="lg:hidden p-2 rounded-lg hover:bg-accent transition-colors"
            data-testid="sidebar-toggle"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>

          <div className="flex-1 lg:ml-0 ml-2 min-w-0">
            <h1 className="text-base sm:text-xl font-heading font-bold text-foreground truncate">
              Distribuidora Santa Ana
            </h1>
          </div>

          {/* Sininho de notificações central */}
          <NotificationBell />

          {/* Notificação Novos Pedidos (legacy) */}
          {novosPedidos > 0 && (
            <Link to="/dashboardadminromaneio/romaneios" className="relative p-2.5 rounded-lg bg-orange-100 hover:bg-orange-200 transition-all mr-2" data-testid="new-orders-bell">
              <Bell className="w-5 h-5 text-orange-600" />
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center animate-pulse">
                {novosPedidos}
              </span>
            </Link>
          )}

          {/* Botão Tema */}
          <button
            onClick={toggleTheme}
            data-testid="theme-toggle"
            className="p-2.5 rounded-lg bg-accent hover:bg-accent/80 transition-all hover:scale-105 active:scale-95 shadow-md"
            title={theme === 'light' ? 'Modo Escuro' : 'Modo Claro'}
          >
            {theme === 'light' ? (
              <Moon className="w-5 h-5 text-foreground" />
            ) : (
              <Sun className="w-5 h-5 text-primary" />
            )}
          </button>
        </header>

        {/* Page Content */}
        <main className="portal-page-bg flex-1 overflow-auto p-3 sm:p-4 lg:p-6">
          <Outlet />
        </main>
      </div>

      {/* Chat flutuante */}
      <ChatWidget />

      {/* Mobile Sidebar Overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}
    </div>
  );
};
